<script setup>
import { ref, watch } from 'vue'

const props = defineProps({
  modelValue:  { type: String, default: '' },
  label:       { type: String, default: 'Search resources' },
  inputId:     { type: String, default: 'resource-search' },
  placeholder: { type: String, default: 'Search by title or description…' },
})

const emit = defineEmits(['search'])

const draft = ref(props.modelValue)

// Keep the draft in sync when the committed term changes from outside
// (e.g. "Clear filters" resets the parent's query ref).
watch(() => props.modelValue, (value) => { draft.value = value })

function submit() {
  emit('search', draft.value.trim())
}
</script>

<template>
  <form class="search" role="search" @submit.prevent="submit">
    <label class="label" :for="inputId">{{ label }}</label>
    <div class="bar">
      <input
        :id="inputId"
        v-model="draft"
        class="query"
        type="search"
        :placeholder="placeholder"
      />
      <button type="submit" class="submit">
        Search
        <svg class="icon" viewBox="0 0 20 20" width="16" height="16" fill="none" aria-hidden="true">
          <circle cx="8.5" cy="8.5" r="6" stroke="currentColor" stroke-width="1.8" />
          <path d="M13.3 13.3 18 18" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
        </svg>
      </button>
    </div>
  </form>
</template>

<style lang="scss" scoped>
.search {
  .label {
    display: block;
    font-weight: 600;
    color: var(--color-ink);
    margin-bottom: $space-2;
  }

  // One integrated bar — the border/radius/focus-ring live here, not on
  // .query, so the submit button can sit flush inside it rather than as a
  // separate element beside it. overflow: hidden clips the button's outer
  // corners to the bar's own radius (same "meets the card/bar radius"
  // treatment as the Home cards' image corners).
  //
  // Named .bar/.query rather than the more obvious .field/.input — those
  // names collide with the generic, unscoped .field/.input patterns in
  // _components.scss (flex-direction: column, fixed height, its own border
  // + radius, etc.), and scoped styles only win the cascade property-by-
  // property, not rule-by-rule — any property this component didn't
  // explicitly set (e.g. flex-direction) fell through to the global rule.
  .bar {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    max-width: 480px;
    border: 1px solid var(--color-border);
    @include rounded($radius);
    overflow: hidden;
    background: var(--color-surface);
    @include transition(border-color, box-shadow);

    &:focus-within { @include focus-ring; }
  }

  .query {
    flex: 1;
    min-width: 0;
    padding: $space-3 $space-4;
    border: none;
    background: transparent;
    font-family: $font-sans;
    font-size: $font-size-base;
    color: var(--color-ink);

    &::placeholder { color: var(--color-text-faint); }
    &:focus        { outline: none; }   // ring now shown on .bar via :focus-within
  }

  // Content-sized, never stretched — just wide enough for the label + icon.
  .submit {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: $space-2;
    padding: $space-3 $space-4;
    border: none;
    background: var(--color-primary);
    color: var(--color-on-primary);
    font-family: $font-sans;
    font-weight: 600;
    font-size: $font-size-cap;
    white-space: nowrap;
    cursor: pointer;
    @include transition(opacity);

    .icon { flex: none; }

    &:hover         { opacity: .9; }
    &:focus-visible { @include focus-ring; }
    &:active        { transform: translateY(1px); }
  }
}
</style>
